/*
 * FLUFFMALLOWS → HORIZON CART BRIDGE
 * ----------------------------------
 * Adds a variant without reloading the page, then sends Horizon the exact
 * cart event it expects. The event carries both the new cart totals and the
 * freshly rendered drawer HTML, so the badge, line items and total all update
 * immediately.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** Return every cart section Horizon currently has on the page. */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
}

/** Convert a plain properties object into Shopify form fields. */
function appendProperties(formData, properties) {
  for (const [name, value] of Object.entries(properties || {})) {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(`properties[${name}]`, String(value));
    }
  }
}

/**
 * Add one Fluff item and resolve only after Horizon has enough information
 * to redraw its cart UI. Callers receive Shopify's add response.
 */
async function addItem(variantId, quantity = 1, properties = {}) {
  if (!variantId) throw new Error('Choose a Shopify product in the Theme Editor first.');

  const sections = cartSectionIds();
  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', String(quantity));
  if (sections.length) formData.append('sections', sections.join(','));
  formData.append('sections_url', window.location.pathname);
  appendProperties(formData, properties);

  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(variantId), quantity: Number(quantity) }],
    promise: deferred.promise,
  });
  document.dispatchEvent(event);

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const added = await response.json();
    if (!response.ok || added.status) {
      throw new Error(added.description || added.message || 'This item could not be added.');
    }

    // The add response contains section HTML, while /cart.js contains the
    // authoritative item count and totals. Horizon needs both.
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    if (!cartResponse.ok) throw new Error('The cart could not be refreshed.');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: added.sections || {},
        items: cart.items,
        itemCount: cart.item_count,
        source: 'fluff-cart',
        didError: false,
      },
    });

    // Keep this small compatibility event for the Fluff toast and animation.
    document.dispatchEvent(new CustomEvent('fluff:cart-added', { detail: { added, cart } }));
    return added;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Add several flavour products in one Shopify request. Build a Box uses this
 * so each selected flavour remains its own inventory-backed cart line while
 * Horizon still receives one coherent cart update event.
 */
async function addItems(items = []) {
  const normalized = items
    .map((item) => ({
      id: Number(item.variantId || item.id),
      quantity: Math.max(1, Number(item.quantity || 1)),
      properties: item.properties || {},
    }))
    .filter((item) => item.id);

  if (!normalized.length) throw new Error('Choose at least one available flavour.');

  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: normalized.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    promise: deferred.promise,
  });
  document.dispatchEvent(event);

  try {
    const root = window.Shopify?.routes?.root || '/';
    const payload = {
      items: normalized,
      sections_url: window.location.pathname,
    };
    if (sections.length) payload.sections = sections;
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const added = await response.json();
    if (!response.ok || added.status) {
      throw new Error(added.description || added.message || 'These flavours could not be added.');
    }

    // Fetch the authoritative cart after the batch add, then resolve the
    // same native Horizon event used by the single-item helper.
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    if (!cartResponse.ok) throw new Error('The cart could not be refreshed.');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: added.sections || {},
        items: cart.items,
        itemCount: cart.item_count,
        source: 'fluff-cart',
        didError: false,
      },
    });

    document.dispatchEvent(new CustomEvent('fluff:cart-added', { detail: { added, cart } }));
    return added;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

window.FluffCart = { addItem, addItems };
document.dispatchEvent(new CustomEvent('fluff:cart-ready'));
